import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../../../../services/google-analytics.service';
import { GoogleIntegrationService } from '../../../../services/google-integration.service';
import { GoogleSearchConsoleService } from '../../../../services/google-search-console.service';
import GoogleIntegrationDetails from '../../../../shared/models/google/google-integration-details';
import { GoogleIntegrationSetupStatus } from '../../../../shared/models/google/google-integration-setup-status';
import { GoogleIntegrationSiteStatuses } from '../../../../shared/models/google/google-integration-site-statuses';
import GoogleIntegrationAccountInformation from '../../../../shared/models/google/google-integration-account-information';
import { GoogleIntegrationDiagnosticComponent } from '../google-integration-diagnostic/google-integration-diagnostic.component';

@Component({
  selector: 'app-google-integration-info',
  templateUrl: './google-integration-info.component.html',
  styleUrl: './google-integration-info.component.scss'
})
export class GoogleIntegrationInfoComponent implements OnInit {
  @Input() customerId: number;
  @Input() isReadOnly = false;

  hasGoogleAnalyticsAccess: boolean;
  canIndexInGoogleSearchConsole: boolean;
  hasCmsAdminCredentials: boolean;
  integrationSetupStatus: GoogleIntegrationSetupStatus;
  isLoading: boolean;
  isOrderingIntegration: boolean;
  isRevokingGoogleAnalyticsAccess: boolean;
  isCmsAdminCredentialsVerified: boolean;
  isRevokeAnalyticsConfirmed: boolean;
  hasGoogleBusinessProfileAccess: boolean;
  canOrderGoogleIntegrationSetup: boolean;
  isGoogleIntegrationSetupRequested: boolean;
  canOrderGoogleIntegrationSetupRedo: boolean;
  isOrderingIntegrationRedo: boolean;
  isClosingRedo: boolean;
  isGoogleIntegrationSetupRedoInProgress: boolean;
  isRedoOrder: boolean;
  isGoogleIntegrationSetupInProgress: boolean;
  isGoogleIntegrationSetupCompleted: boolean;
  isGoogleIntegrationSetupMissingCredentials: boolean;
  summarySubtitle: string;
  gtmId: string;
  scVerificationHtmlTag: string;
  gtmTagStatus: GoogleIntegrationSiteStatuses = GoogleIntegrationSiteStatuses.Unknown;
  verificationSnippetStatus: GoogleIntegrationSiteStatuses = GoogleIntegrationSiteStatuses.Unknown;
  sitePresenceLastCheckedDate: string | null = null;
  isSitePresenceCheckInProgress = false;
  isRequestingSitePresenceCheck = false;
  isRefreshingSitePresenceStatus = false;
  lastCheckedExpectedGtmId: string | null = null;
  lastCheckedExpectedVerificationToken: string | null = null;
  accountInformation: GoogleIntegrationAccountInformation;
  readonly googleBusinessProfileConnectorUrl = 'https://dataconnector.io/';
  readonly GoogleIntegrationSiteStatuses = GoogleIntegrationSiteStatuses;
  private static readonly verificationTokenRegex = /content\s*=\s*["']([^"']+)["']/i;

  private correctionTriggered = false;

  @ViewChild('cmsAdminDialogTemplate', { static: true })
  private cmsAdminDialogTemplate: TemplateRef<unknown>;

  @ViewChild('revokeAnalyticsDialogTemplate', { static: true })
  private revokeAnalyticsDialogTemplate: TemplateRef<unknown>;

  @ViewChild('accountInfoDialogTemplate', { static: true })
  private accountInfoDialogTemplate: TemplateRef<unknown>;

  @ViewChild('closeRedoDialogTemplate', { static: true })
  private closeRedoDialogTemplate: TemplateRef<unknown>;

  constructor(
    private modalService: NgbModal,
    private googleAnalyticsService: GoogleAnalyticsService,
    private googleIntegrationService: GoogleIntegrationService,
    private googleSearchConsoleService: GoogleSearchConsoleService
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  checkSitePresence(): void {
    this.isRequestingSitePresenceCheck = true;
    this.googleIntegrationService.checkSitePresence(this.customerId)
      .pipe(finalize(() => this.isRequestingSitePresenceCheck = false))
      .subscribe({
        next: () => {
          toastr.info('Site presence check queued.');
          this.isSitePresenceCheckInProgress = true;
        },
        error: (error: string) => toastr.error(error)
      });
  }

  refreshSitePresenceStatus(): void {
    this.isRefreshingSitePresenceStatus = true;
    this.googleIntegrationService.getDetails(this.customerId)
      .pipe(finalize(() => this.isRefreshingSitePresenceStatus = false))
      .subscribe({
        next: (details) => {
          const wasInProgress = this.isSitePresenceCheckInProgress;
          this.applySitePresenceFromDetails(details);

          if (wasInProgress && !this.isSitePresenceCheckInProgress) {
            toastr.success('Site presence check complete.');
          }
        },
        error: (error: string) => toastr.error(error)
      });
  }

  checkSiteForGoogleIntegrations(): void {
    if (this.isSitePresenceCheckInProgress) {
      this.refreshSitePresenceStatus();
    } else {
      this.checkSitePresence();
    }
  }

  getSitePresenceButtonText(): string {
    if (this.isRequestingSitePresenceCheck) {
      return 'Checking…';
    }

    if (this.isRefreshingSitePresenceStatus) {
      return 'Refreshing…';
    }

    if (this.isSitePresenceCheckInProgress) {
      return 'Refresh Status';
    }

    return this.sitePresenceLastCheckedDate ? 'Recheck Site' : 'Check Site';
  }

  get isSitePresenceButtonDisabled(): boolean {
    return this.isRequestingSitePresenceCheck || this.isRefreshingSitePresenceStatus;
  }

  orderGoogleIntegrationSetup(): void {
    this.googleIntegrationService.hasCmsAdminCredentials(this.customerId)
      .subscribe({
        next: (hasCredentials: boolean) => {
          this.hasCmsAdminCredentials = hasCredentials;
          this.updateUiState();

          const isInProgressOrCompleted = this.integrationSetupStatus === GoogleIntegrationSetupStatus.InProgress
            || this.integrationSetupStatus === GoogleIntegrationSetupStatus.Completed;

          if (isInProgressOrCompleted) {
            toastr.info('Google Integration Setup is already in progress or completed.');
            return;
          }

          if (!hasCredentials) {
            toastr.error('CMS Admin credentials are required before ordering Google Integration Setup.');
            return;
          }

          this.isRedoOrder = false;
          this.isCmsAdminCredentialsVerified = false;
          this.modalService.open(this.cmsAdminDialogTemplate, { centered: true });
        },
        error: (error: string) => toastr.error(error)
      });
  }

  confirmGoogleIntegrationSetupOrder(modalRef: NgbModalRef): void {
    if (!this.isCmsAdminCredentialsVerified) {
      return;
    }

    modalRef.close();

    if (this.isRedoOrder) {
      this.submitGoogleIntegrationRedoOrder();
    } else {
      this.submitGoogleIntegrationOrder();
    }
  }

  revokeGoogleAnalyticsAccess(): void {
    if (!this.hasGoogleAnalyticsAccess || this.isRevokingGoogleAnalyticsAccess) {
      return;
    }

    this.isRevokeAnalyticsConfirmed = false;
    this.modalService.open(this.revokeAnalyticsDialogTemplate, { centered: true });
  }

  orderGoogleIntegrationSetupRedo(): void {
    this.isRedoOrder = true;
    this.isCmsAdminCredentialsVerified = false;
    this.modalService.open(this.cmsAdminDialogTemplate, { centered: true });
  }

  closeGoogleIntegrationSetupRedo(): void {
    this.modalService.open(this.closeRedoDialogTemplate, { centered: true });
  }

  confirmCloseGoogleIntegrationSetupRedo(modalRef: NgbModalRef): void {
    if (this.isClosingRedo) {
      return;
    }

    this.isClosingRedo = true;

    this.googleIntegrationService.closeIntegrationSetupRedo(this.customerId)
      .pipe(finalize(() => this.isClosingRedo = false))
      .subscribe({
        next: () => {
          modalRef.close();
          toastr.success('Google Integration Redo has been closed.');
          this.isGoogleIntegrationSetupRedoInProgress = false;
          this.canOrderGoogleIntegrationSetupRedo = true;
          this.updateUiState();
        },
        error: (error: string) => toastr.error(error)
      });
  }

  openAccountInformation(): void {
    this.modalService.open(this.accountInfoDialogTemplate, { centered: true });
  }

  openDiagnostic(): void {
    const modalRef = this.modalService.open(GoogleIntegrationDiagnosticComponent, { centered: true, beforeDismiss: () => false });
    modalRef.componentInstance.customerId = this.customerId;
    modalRef.componentInstance.isReadOnly = this.isReadOnly
      || !this.isGoogleIntegrationSetupCompleted
      || this.isGoogleIntegrationSetupRedoInProgress;

    modalRef.closed
      .subscribe(({ correctionTriggered, hasFreshDiagnostic }) => {
        this.correctionTriggered ||= correctionTriggered;
        
        if (this.correctionTriggered && hasFreshDiagnostic) {
          this.loadData();
          this.correctionTriggered = false;
        }
      });
  }

  copyGoogleBusinessProfileConnectorLink(): void {
    this.copyToClipboard(this.googleBusinessProfileConnectorUrl);
  }

  copyVerificationSnippet(): void {
    this.copyToClipboard(this.scVerificationHtmlTag);
  }

  copyGtmId(): void {
    this.copyToClipboard(this.gtmId);
  }

  confirmRevokeGoogleAnalyticsAccess(modalRef: NgbModalRef): void {
    if (!this.isRevokeAnalyticsConfirmed || this.isRevokingGoogleAnalyticsAccess) {
      return;
    }

    this.isRevokingGoogleAnalyticsAccess = true;
    this.googleAnalyticsService.revokeAccess(this.customerId)
      .pipe(
        finalize(() => {
          this.isRevokingGoogleAnalyticsAccess = false;
        }),
      )
      .subscribe({
        next: () => {
          modalRef.close();
          toastr.success('Analytics access revoked.');
          this.refreshGoogleAnalyticsAccessStatus();
        },
        error: (error: string) => toastr.error(error)
      });
  }

  isStatusUnknown(status: GoogleIntegrationSiteStatuses): boolean {
    return status === GoogleIntegrationSiteStatuses.Unknown;
  }

  getSiteFoundText(status: GoogleIntegrationSiteStatuses): string {
    switch (status) {
      case GoogleIntegrationSiteStatuses.Pass:
        return 'Found';
      case GoogleIntegrationSiteStatuses.Fail:
        return 'Not Found';
      case GoogleIntegrationSiteStatuses.Unreachable:
        return '- Site Unreachable';
      case GoogleIntegrationSiteStatuses.NotConfigured:
        return '- Not Configured';
      default:
        return '';
    }
  }

  getGtmStatusSubject(): string {
    return this.lastCheckedExpectedGtmId || 'GTM Tag';
  }

  get isGtmTagOutOfSync(): boolean {
    if (!this.lastCheckedExpectedGtmId || !this.gtmId) {
      return false;
    }

    return this.lastCheckedExpectedGtmId.toUpperCase() !== this.gtmId.toUpperCase();
  }

  get isVerificationSnippetOutOfSync(): boolean {
    if (!this.lastCheckedExpectedVerificationToken || !this.scVerificationHtmlTag) {
      return false;
    }

    const match = GoogleIntegrationInfoComponent.verificationTokenRegex.exec(this.scVerificationHtmlTag);
    const currentToken = match ? match[1] : null;
    
    if (!currentToken) {
      return false;
    }

    return currentToken !== this.lastCheckedExpectedVerificationToken;
  }

  private submitGoogleIntegrationOrder(): void {
    if (this.isOrderingIntegration) {
      return;
    }

    this.isOrderingIntegration = true;

    this.googleIntegrationService.orderIntegrationSetup(this.customerId)
      .pipe(finalize(() => this.isOrderingIntegration = false))
      .subscribe({
        next: (wasCreated: boolean) => {
          if (wasCreated) {
            toastr.success('Google Integration Setup has been ordered.');
          } else {
            toastr.info('Google Integration Setup is already in progress or completed.');
          }

          this.refreshIntegrationSetupStatus();
        },
        error: (error: string) => toastr.error(error)
      });
  }

  private submitGoogleIntegrationRedoOrder(): void {
    if (this.isOrderingIntegrationRedo) {
      return;
    }

    this.isOrderingIntegrationRedo = true;

    this.googleIntegrationService.orderIntegrationSetupRedo(this.customerId)
      .pipe(finalize(() => this.isOrderingIntegrationRedo = false))
      .subscribe({
        next: (wasCreated: boolean) => {
          if (wasCreated) {
            toastr.success('Google Integration Redo has been ordered.');
            this.canOrderGoogleIntegrationSetupRedo = false;
            this.isGoogleIntegrationSetupRedoInProgress = true;
            this.updateUiState();
          } else {
            toastr.info('Google Integration Redo could not be ordered.');
          }
        },
        error: (error: string) => toastr.error(error)
      });
  }

  private refreshIntegrationSetupStatus(): void {
    this.googleIntegrationService.getIntegrationSetupStatus(this.customerId)
      .subscribe({
        next: (status: GoogleIntegrationSetupStatus) => {
          this.integrationSetupStatus = status;
          this.updateUiState();
        },
        error: (error: string) => toastr.error(error)
      });
  }

  private refreshGoogleAnalyticsAccessStatus(): void {
    this.googleAnalyticsService.checkAuthorization(this.customerId)
      .subscribe({
        next: (hasGoogleAnalyticsAccess: boolean) => {
          this.hasGoogleAnalyticsAccess = hasGoogleAnalyticsAccess;
          this.updateUiState();
        },
        error: (error: string) => toastr.error(error)
      });
  }

  private updateUiState(): void {
    const integrationSetupStatus = this.integrationSetupStatus ?? GoogleIntegrationSetupStatus.None;

    this.isGoogleIntegrationSetupRequested =
      integrationSetupStatus === GoogleIntegrationSetupStatus.Requested && this.hasCmsAdminCredentials;
    this.isGoogleIntegrationSetupInProgress = integrationSetupStatus === GoogleIntegrationSetupStatus.InProgress;
    this.isGoogleIntegrationSetupCompleted = integrationSetupStatus === GoogleIntegrationSetupStatus.Completed;
    this.isGoogleIntegrationSetupMissingCredentials =
      !this.hasCmsAdminCredentials && (
        integrationSetupStatus === GoogleIntegrationSetupStatus.None
        || integrationSetupStatus === GoogleIntegrationSetupStatus.Requested
      );
    this.canOrderGoogleIntegrationSetup = (
      integrationSetupStatus === GoogleIntegrationSetupStatus.None
      || integrationSetupStatus === GoogleIntegrationSetupStatus.Requested
    ) && this.hasCmsAdminCredentials;

    if (this.isGoogleIntegrationSetupInProgress) {
      this.summarySubtitle = 'Integration setup is currently being processed.';
    } else if (this.isGoogleIntegrationSetupRedoInProgress) {
      this.summarySubtitle = 'Integration setup redo is currently being processed.';
    } else if (this.isGoogleIntegrationSetupMissingCredentials) {
      this.summarySubtitle = 'CMS Admin Access credentials are required before setup can begin.';
    } else {
      this.summarySubtitle = '';
    }
  }

  private applySitePresenceFromDetails(details: GoogleIntegrationDetails): void {
    // Only synthesize NotConfigured when the server has no real status to report (Unknown).
    // If the server returned an actual check outcome (Pass / Fail / Unreachable / NotConfigured),
    // show that directly even when the current provisioned value is missing.
    const serverGtmStatus = details.GtmTagStatus ?? GoogleIntegrationSiteStatuses.Unknown;
    this.gtmTagStatus = !details.GtmId && serverGtmStatus === GoogleIntegrationSiteStatuses.Unknown
      ? GoogleIntegrationSiteStatuses.NotConfigured
      : serverGtmStatus;

    const serverVerificationStatus = details.VerificationSnippetStatus ?? GoogleIntegrationSiteStatuses.Unknown;
    this.verificationSnippetStatus = !details.ScVerificationHtmlTag && serverVerificationStatus === GoogleIntegrationSiteStatuses.Unknown
      ? GoogleIntegrationSiteStatuses.NotConfigured
      : serverVerificationStatus;

    this.sitePresenceLastCheckedDate = details.SitePresenceLastCheckedDate ?? null;
    this.isSitePresenceCheckInProgress = !!details.IsSitePresenceCheckInProgress;
    this.lastCheckedExpectedGtmId = details.LastCheckedExpectedGtmId ?? null;
    this.lastCheckedExpectedVerificationToken = details.LastCheckedExpectedVerificationToken ?? null;
  }

  private copyToClipboard(value: string): void {
    if (!value) {
      return;
    }

    navigator.clipboard.writeText(value)
      .then(() => toastr.success('Copied to clipboard'))
      .catch((error: unknown) => {
        toastr.error('Failed to copy to clipboard');
        console.error(error);
      });
  }

  private loadData(): void {
    this.isLoading = true;

    forkJoin({
      hasGoogleAnalyticsAccess: this.googleAnalyticsService.checkAuthorization(this.customerId),
      hasGoogleBusinessProfileAccess: this.googleIntegrationService.hasGoogleBusinessProfileAccess(this.customerId),
      canIndexInGoogleSearchConsole: this.googleSearchConsoleService.canIndex(this.customerId),
      summary: this.googleIntegrationService.getIntegrationSummary(this.customerId),
      accountInformation: this.googleIntegrationService.getAccountInformation(this.customerId),
      details: this.googleIntegrationService.getDetails(this.customerId)
        .pipe(catchError(() => of({} as GoogleIntegrationDetails)))
    }).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: ({ hasGoogleAnalyticsAccess, hasGoogleBusinessProfileAccess, canIndexInGoogleSearchConsole, summary, accountInformation, details }) => {
          this.hasGoogleAnalyticsAccess = hasGoogleAnalyticsAccess;
          this.hasGoogleBusinessProfileAccess = hasGoogleBusinessProfileAccess;
          this.canIndexInGoogleSearchConsole = canIndexInGoogleSearchConsole;
          this.accountInformation = accountInformation;
          this.hasCmsAdminCredentials = summary.HasCmsAdminCredentials;
          this.integrationSetupStatus = summary.IntegrationSetupStatus;
          this.canOrderGoogleIntegrationSetupRedo = summary.CanOrderIntegrationSetupRedo;
          this.isGoogleIntegrationSetupRedoInProgress = summary.IsIntegrationSetupRedoInProgress;
          this.gtmId = details.GtmId;
          this.scVerificationHtmlTag = details.ScVerificationHtmlTag;
          this.applySitePresenceFromDetails(details);
          this.updateUiState();
        },
        error: (error: string) => {
          toastr.error(error);
        }
      });
  }
}
